import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import JudgeDashboard from '@/pages/JudgeDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import { useTheme } from '@/hooks/useTheme';
import '@/App.css';

function readAuthFromStorage() {
  try {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      return JSON.parse(userData);
    }
  } catch (e) {
    // fall through
  }
  return null;
}

function App() {
  // Initialize user synchronously from localStorage so new tabs opened via
  // window.open() don't flash the login screen (which would strip query params)
  const [user, setUser] = useState(readAuthFromStorage);
  const { theme } = useTheme();

  // Belt-and-suspenders: re-sync from localStorage on mount and whenever the
  // storage changes (e.g. another tab logged in). Handles any edge case where
  // the synchronous init might miss the stored auth (stale bundle cache, etc.)
  useEffect(() => {
    if (document.title === 'Emergent | Fullstack App' || !document.title) {
      document.title = 'Piala Suara Serumpun 2026';
    }
    const hydrate = () => {
      const stored = readAuthFromStorage();
      if (stored && !user) {
        setUser(stored);
      } else if (!stored && user) {
        setUser(null);
      }
    };
    hydrate();
    window.addEventListener('storage', hydrate);
    window.addEventListener('focus', hydrate);
    return () => {
      window.removeEventListener('storage', hydrate);
      window.removeEventListener('focus', hydrate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Return true if there's a token in localStorage — used as a last-line check
  // before redirecting to /login so a just-opened tab (whose state may not be
  // hydrated yet) does not lose its query params to a Navigate redirect.
  const hasStoredAuth = () => {
    try {
      return Boolean(localStorage.getItem('token') && localStorage.getItem('user'));
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              !user ? (
                hasStoredAuth() ? (
                  <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
                    <div className="text-zinc-400">Loading...</div>
                  </div>
                ) : (
                  <Login onLogin={handleLogin} />
                )
              ) : user.role === 'admin' ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/judge" replace />
              )
            }
          />
          <Route
            path="/judge"
            element={
              user && user.role === 'judge' ? (
                <JudgeDashboard user={user} onLogout={handleLogout} />
              ) : hasStoredAuth() ? (
                <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
                  <div className="text-zinc-400">Loading...</div>
                </div>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && user.role === 'admin' ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : hasStoredAuth() ? (
                <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
                  <div className="text-zinc-400">Loading...</div>
                </div>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster theme={theme} />
    </div>
  );
}

export default App;
